/** 日历和甘特图共享按天拖动：移动条身平移，拖两端修改工期，松开一次提交。
 * 日期使用纯日历计算；取消不保存，保存时锁住交互。权限/依赖/并发以服务端为准，
 * 不把预览写入正式数据，因此失败后自然恢复原安排，也不会留下半次本地改期。
 */
import { useEffect, useRef, useState } from 'react';
import { lastWorkDay, shiftDay } from './scheduleCalendar';

const distance = (a, b) => Math.round((Date.parse(`${a}T12:00:00Z`) - Date.parse(`${b}T12:00:00Z`)) / 86400000);
const working = (date, week) => !week || week.includes((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7);
const roll = (date, direction, week) => {
  for (let i = 0; i < 7 && !working(date, week); i++) date = shiftDay(date, direction);
  return date;
};

/** 平移保留施工工作日数，避免跨星期日后悄悄缩短工期；调整边界最短保留一天。 */
export function scheduleDragDates(task, mode, delta) {
  const first = task.startAt.slice(0, 10), last = lastWorkDay(task), effectiveWeek = task.scheduleWorkdays || task.workdays;
  const week = effectiveWeek?.length ? effectiveWeek : null;
  const direction = delta < 0 ? -1 : 1;
  let start = first, end = last;
  if (!delta) return { start, end };
  if (mode === 'move') {
    start = roll(shiftDay(first, delta), direction, week);
    if (!week) end = shiftDay(last, distance(start, first));
    else {
      const days = distance(last, first) + 1;
      let remaining = Array.from({ length: days }, (_, i) => shiftDay(first, i)).filter(date => working(date, week)).length - 1;
      end = start;
      while (remaining > 0) { end = shiftDay(end, 1); if (working(end, week)) remaining--; }
    }
  } else if (mode === 'start') start = roll(shiftDay(first, delta), direction, week);
  else end = roll(shiftDay(last, delta), direction, week);
  if (start > end) { if (mode === 'start') start = end; else end = start; }
  return { start, end };
}

/** 预览仅投影到图表，刷新/取消立即回到服务端数据；不改变任务版本或实际派工记录。 */
export function scheduleDragPreview(tasks, preview) {
  return preview ? tasks.map(task => task.id === preview.id ? { ...task,
    startAt: `${preview.start}T00:00`, endAt: `${shiftDay(preview.end, 1)}T00:00` } : task) : tasks;
}

/** onSave(task, dates) 返回保存 Promise；context 变化中止未提交手势，防止跨筛选误操作。 */
export default function useScheduleDrag({ context, onSave, enabled }) {
  const [preview, setPreview] = useState(null), [saving, setSaving] = useState(false);
  const active = useRef(null), busy = useRef(false), suppress = useRef(0), mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; active.current?.cancel(); }; }, []);
  useEffect(() => { active.current?.cancel(); setPreview(null); }, [context]);
  const canDrag = task => enabled && task.canEdit && task.status === 'planned' && !task.rescheduleRequest && !task.planAccepted && !(task.progressPercent > 0) && !saving;
  const save = async (task, next) => {
    if (busy.current) return;
    if (next.start === task.startAt.slice(0, 10) && next.end === lastWorkDay(task)) { setPreview(null); return; }
    busy.current = true; setSaving(true);
    try { await onSave(task, next); }
    finally { busy.current = false; if (mounted.current) { setSaving(false); setPreview(null); } }
  };
  const bind = (task, gesture, { dayWidth, calendar = false, anchorDate }) => ({
    ...gesture,
    'data-schedule-draggable': canDrag(task) ? 'true' : undefined,
    'aria-busy': saving || undefined,
    'aria-keyshortcuts': canDrag(task) ? 'Alt+ArrowLeft Alt+ArrowRight Alt+Shift+ArrowLeft Alt+Shift+ArrowRight' : gesture['aria-keyshortcuts'],
    onClick: event => { if (Date.now() < suppress.current) { event.preventDefault(); event.stopPropagation(); return; } gesture.onClick?.(event); },
    onDoubleClick: event => { if (Date.now() < suppress.current) return; gesture.onDoubleClick?.(event); },
    onKeyDown: event => {
      if (canDrag(task) && event.altKey && ['ArrowLeft', 'ArrowRight'].includes(event.key)) {
        event.preventDefault(); event.stopPropagation();
        save(task, scheduleDragDates(task, event.ctrlKey ? 'start' : event.shiftKey ? 'end' : 'move', event.key === 'ArrowLeft' ? -1 : 1));
      } else gesture.onKeyDown?.(event);
    },
    onPointerDown: event => {
      gesture.onPointerDown?.(event);
      if (!canDrag(task) || busy.current || event.button !== 0) return;
      const element = event.currentTarget, mode = event.target.closest('[data-resize]')?.dataset.resize || 'move';
      const region = calendar ? element.closest('.schedule-calendar') : element.closest('.schedule-gantt-scroll');
      const x = event.clientX, y = event.clientY, scrollLeft = region?.scrollLeft || 0;
      const width = calendar ? 1 : element.closest('.schedule-gantt-track').getBoundingClientRect().width / dayWidth;
      const initialCell = calendar && [...region.querySelectorAll('[data-schedule-date]')].find(cell => {
        const rect = cell.getBoundingClientRect(); return x >= rect.left && x < rect.right && y >= rect.top && y < rect.bottom;
      });
      const originDate = initialCell?.dataset?.scheduleDate || anchorDate;
      let moved = false, next = null;
      const cleanup = () => {
        window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
        window.removeEventListener('pointercancel', cancel); window.removeEventListener('keydown', escape); window.removeEventListener('blur', cancel);
        active.current = null;
      };
      const cancel = () => { cleanup(); if (mounted.current) setPreview(null); if (moved) suppress.current = Date.now() + 600; };
      const move = e => {
        if (e.pointerId !== event.pointerId) return;
        if (!moved && Math.hypot(e.clientX - x, e.clientY - y) < 5) return;
        moved = true; e.preventDefault();
        let delta = Math.round((e.clientX - x + (region?.scrollLeft || 0) - scrollLeft) / width);
        if (calendar) {
          const hit = [...(region?.querySelectorAll('[data-schedule-date]') || [])].find(cell => {
            const rect = cell.getBoundingClientRect(); return e.clientX >= rect.left && e.clientX < rect.right && e.clientY >= rect.top && e.clientY < rect.bottom;
          });
          if (!hit) return;
          delta = distance(hit.dataset.scheduleDate, originDate);
        } else if (region) {
          const rect = region.getBoundingClientRect();
          if (e.clientX > rect.right - 32) region.scrollLeft += 20;
          if (e.clientX < rect.left + 220) region.scrollLeft -= 20;
        }
        next = scheduleDragDates(task, mode, delta);
        setPreview({ id: task.id, mode, delta, ...next });
      };
      const up = e => {
        if (e.pointerId !== event.pointerId) return;
        cleanup();
        if (moved) { suppress.current = Date.now() + 600; if (next) save(task, next); else setPreview(null); }
      };
      const escape = e => { if (e.key === 'Escape') { e.preventDefault(); cancel(); } };
      active.current?.cancel(); active.current = { cancel };
      window.addEventListener('pointermove', move, { passive: false }); window.addEventListener('pointerup', up);
      window.addEventListener('pointercancel', cancel); window.addEventListener('keydown', escape); window.addEventListener('blur', cancel);
    },
  });
  return { bind, canDrag, preview, saving };
}
